import { toArray } from '../../lib/utils/to-array';
import { toTrimmedNonEmptyString } from '../../lib/utils/type-guards';
import {
  MODEL_ROLES,
  type ModelCapability,
  type ModelDefinition,
  type ModelProvider,
  type ModelRole,
  type ModelTier,
} from './types';

export const AUTO_MODEL_SENTINEL = 'auto';

type ModelBootstrapKey = 'premium' | 'standard' | 'default' | 'fallback';

type ModelBootstrapConfig = Partial<Record<ModelBootstrapKey, string>>;

const getBootstrapConfig = (): ModelBootstrapConfig | undefined => {
  const globalCandidate = globalThis as typeof globalThis & {
    __SWEETISTICS_MODEL_OVERRIDES__?: ModelBootstrapConfig;
  };

  return globalCandidate.__SWEETISTICS_MODEL_OVERRIDES__;
};

const FALLBACK_PREMIUM_MODEL_ID = 'model.reasoning.premium';
const FALLBACK_STANDARD_MODEL_ID = 'model.reasoning.fast';

const normalizeConfiguredModelId = (value: string | undefined, fallback: string): string => {
  const trimmed = toTrimmedNonEmptyString(value);
  if (!trimmed || trimmed === AUTO_MODEL_SENTINEL) {
    return fallback;
  }
  return trimmed;
};

const readBootstrapValue = (key: ModelBootstrapKey): string | undefined => {
  const config = getBootstrapConfig();
  if (!config) {
    return;
  }
  const rawValue = config[key];
  const trimmed = toTrimmedNonEmptyString(rawValue);
  if (!trimmed) {
    return;
  }
  return trimmed;
};

const bootstrapConfiguredPremiumId = readBootstrapValue('premium');
const bootstrapConfiguredStandardId = readBootstrapValue('standard');

const CONFIGURED_PREMIUM_MODEL_ID = normalizeConfiguredModelId(bootstrapConfiguredPremiumId, FALLBACK_PREMIUM_MODEL_ID);
const CONFIGURED_STANDARD_MODEL_ID = normalizeConfiguredModelId(
  bootstrapConfiguredStandardId,
  FALLBACK_STANDARD_MODEL_ID
);

const inferProviderFromModelId = (rawModelId: string | null | undefined): ModelProvider => {
  const modelId = toTrimmedNonEmptyString(rawModelId);
  if (!modelId) {
    return 'custom';
  }
  const provider = modelId.split('/')[0]?.toLowerCase();
  if (
    provider === 'openai' ||
    provider === 'anthropic' ||
    provider === 'google' ||
    provider === 'meta' ||
    provider === 'xai'
  ) {
    return provider;
  }
  if (provider === 'mistral') {
    return 'mistral';
  }
  return 'custom';
};

export const MODEL_ID_BY_ROLE: Record<ModelRole, string> = {
  [MODEL_ROLES.PREMIUM]: CONFIGURED_PREMIUM_MODEL_ID,
  [MODEL_ROLES.STANDARD]: CONFIGURED_STANDARD_MODEL_ID,
};

export const PREMIUM_MODEL_ID = MODEL_ID_BY_ROLE[MODEL_ROLES.PREMIUM];
export const STANDARD_MODEL_ID = MODEL_ID_BY_ROLE[MODEL_ROLES.STANDARD];

const BASE_MODEL_DEFINITIONS: readonly ModelDefinition[] = [
  {
    id: 'model.reasoning.premium',
    provider: 'xai',
    providerModelId: 'xai/grok-4-1-fast-reasoning',
    label: 'Grok 4.1 Fast Reasoning',
    description: "xAI's Grok 4.1 Fast preset tuned for complex reasoning workloads",
    tier: 'standard',
    capabilities: ['reasoning', 'speed'],
    defaultEnabled: true,
    isDefault: true,
    isFallback: false,
    aliasKeys: ['MODEL_REASONING_PREMIUM', 'GROK_4FAST_REASONING'],
    alternateIds: ['xai/grok-4-1-fast-reasoning', 'xai/grok-4-1-fast', 'xai/grok-4-fast-reasoning', 'xai/grok-4-fast'],
    defaultConfig: {
      maxTokens: 2_000_000,
    },
  },
  {
    id: 'model.reasoning.balanced',
    provider: 'xai',
    providerModelId: 'xai/grok-4-1-fast-non-reasoning',
    label: 'Grok 4.1 Fast (Default)',
    description: 'Default Grok 4.1 Fast mode tuned for everyday summarization without test-time reasoning overhead.',
    tier: 'standard',
    capabilities: ['speed'],
    defaultEnabled: true,
    isDefault: false,
    isFallback: false,
    aliasKeys: ['MODEL_REASONING_BALANCED', 'GROK_4FAST_DEFAULT'],
    alternateIds: [
      'xai/grok-4-1-fast-non-reasoning',
      'xai/grok-4-1-fast',
      'xai/grok-4-fast-non-reasoning',
      'xai/grok-4-fast',
    ],
    defaultConfig: {
      maxTokens: 2_000_000,
    },
  },
  {
    id: 'model.reasoning.fast',
    provider: 'xai',
    providerModelId: 'xai/grok-4-1-fast-non-reasoning',
    label: 'xAI Grok 4.1 Fast',
    description: 'Latency-focused Grok 4.1 Fast mode without test-time reasoning overhead.',
    tier: 'standard',
    capabilities: ['speed'],
    defaultEnabled: true,
    isDefault: false,
    isFallback: true,
    aliasKeys: ['MODEL_REASONING_FAST', 'GROK_4FAST_NON_REASONING'],
    alternateIds: [
      'xai/grok-4-1-fast-non-reasoning',
      'xai/grok-4-1-fast',
      'xai/grok-4-fast-non-reasoning',
      'xai/grok-4-fast',
    ],
    defaultConfig: {
      maxTokens: 2_000_000,
    },
  },
  {
    id: 'xai/grok-4',
    provider: 'xai',
    label: 'Grok 4',
    description: "xAI's flagship multi-turn reasoning model",
    tier: 'premium',
    capabilities: ['reasoning'],
    defaultEnabled: true,
    isDefault: false,
    isFallback: false,
    aliasKeys: ['GROK_4'],
    alternateIds: [],
    defaultConfig: {
      maxTokens: 256_000,
    },
  },
  {
    id: 'xai/grok-code-fast-1',
    provider: 'xai',
    providerModelId: 'xai/grok-code-fast-1',
    label: 'Grok Code Fast 1',
    description: "xAI's Grok Fast preset optimized for agentic code generation and function execution.",
    tier: 'standard',
    capabilities: ['reasoning', 'speed'],
    defaultEnabled: false,
    isDefault: false,
    isFallback: false,
    aliasKeys: ['GROK_CODE_FAST_1'],
    alternateIds: ['grok-code-fast-1'],
    defaultConfig: {
      maxTokens: 256_000,
    },
  },
  {
    id: 'openai/gpt-5',
    provider: 'openai',
    providerModelId: 'openai/gpt-5',
    label: 'GPT-5',
    description: 'OpenAI’s flagship GPT-5 reasoning model with maximum depth and context.',
    tier: 'premium',
    capabilities: ['reasoning', 'long-context'],
    defaultEnabled: false,
    isDefault: false,
    isFallback: false,
    aliasKeys: ['GPT_5'],
    alternateIds: ['gpt-5'],
    defaultConfig: {
      maxTokens: 400_000,
    },
  },
  {
    id: 'openai/gpt-5-nano',
    provider: 'openai',
    providerModelId: 'openai/gpt-5-nano',
    label: 'GPT-5 Nano',
    description: 'Fast GPT-5 Nano for high-volume summaries and quick iterations.',
    tier: 'standard',
    capabilities: ['speed'],
    defaultEnabled: false,
    isDefault: false,
    isFallback: false,
    aliasKeys: ['GPT_5_NANO'],
    alternateIds: ['gpt-5-nano'],
    defaultConfig: {
      maxTokens: 400_000,
    },
  },
  {
    id: 'anthropic/sonnet-4.5',
    provider: 'anthropic',
    label: 'Claude Sonnet 4.5',
    description: 'Anthropic’s balanced Claude 4.5 model',
    tier: 'standard',
    capabilities: ['long-context'],
    defaultEnabled: true,
    isDefault: false,
    isFallback: false,
    aliasKeys: ['CLAUDE_SONNET_45', 'CLAUDE_SONNET_4'],
    alternateIds: ['anthropic/sonnet-4', 'anthropic/claude-3-sonnet-20_240_229'],
    defaultConfig: {
      maxTokens: 1_000_000,
    },
  },
  {
    id: 'google/gemini-latest',
    provider: 'google',
    providerModelId: 'google/gemini-2.5-pro',
    label: 'Gemini Latest',
    description: "Google's most capable Gemini reasoning model (current release)",
    tier: 'premium',
    capabilities: ['long-context'],
    defaultEnabled: true,
    isDefault: false,
    isFallback: false,
    aliasKeys: ['GEMINI_LATEST', 'GEMINI_25_PRO'],
    alternateIds: ['google/gemini-2.5-pro', 'google/gemini-1.5-pro-latest'],
    defaultConfig: {
      maxTokens: 1_048_576,
    },
  },
];

type ModelDefinitionPatch = Partial<Omit<ModelDefinition, 'id'>> & { provider?: ModelProvider };

const mergeStringSets = <T extends string>(existing?: readonly T[], patch?: readonly T[]): readonly T[] | undefined => {
  const next = new Set<T>();
  if (existing) {
    for (const value of existing) {
      next.add(value);
    }
  }
  if (patch) {
    for (const value of patch) {
      next.add(value);
    }
  }
  if (next.size === 0) {
    return;
  }
  return [...next];
};

const mergeCapabilities = (
  existing?: readonly ModelCapability[],
  patch?: readonly ModelCapability[]
): readonly ModelCapability[] | undefined => mergeStringSets(existing, patch);

const cloneDefaultConfig = (
  config: ModelDefinition['defaultConfig'] | undefined
): ModelDefinition['defaultConfig'] | undefined => {
  if (!config) {
    return;
  }
  const { providerOptions, ...rest } = config;
  const cloned: ModelDefinition['defaultConfig'] = { ...rest };
  if (providerOptions) {
    cloned.providerOptions = { ...providerOptions };
  }
  return cloned;
};

const mergeDefaultConfig = (
  existing?: ModelDefinition['defaultConfig'],
  patch?: ModelDefinition['defaultConfig']
): ModelDefinition['defaultConfig'] => {
  if (!(existing || patch)) {
    return;
  }
  if (!existing) {
    return cloneDefaultConfig(patch);
  }
  if (!patch) {
    return cloneDefaultConfig(existing);
  }

  const merged: ModelDefinition['defaultConfig'] = { ...existing, ...patch };

  const providerOptions: Record<string, unknown> = {};
  if (existing.providerOptions) {
    Object.assign(providerOptions, existing.providerOptions);
  }
  if (patch.providerOptions) {
    Object.assign(providerOptions, patch.providerOptions);
  }

  if (Object.keys(providerOptions).length > 0) {
    return {
      ...merged,
      providerOptions,
    };
  }

  const { providerOptions: _unused, ...configWithoutProviderOptions } = merged;
  return configWithoutProviderOptions;
};

const assignOptionalField = <T extends object, K extends keyof T>(target: T, key: K, value: T[K] | undefined): void => {
  if (value === undefined) {
    delete target[key];
    return;
  }
  target[key] = value;
};

export const PROVIDER_DISPLAY_NAMES: Record<string, string> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  google: 'Google',
  meta: 'Meta',
  xai: 'xAI',
  mistral: 'Mistral',
  custom: 'Custom',
};

const MODEL_SEGMENT_OVERRIDES: Record<string, string> = {
  ai: 'AI',
  claude: 'Claude',
  deepseek: 'DeepSeek',
  flash: 'Flash',
  gemini: 'Gemini',
  gpt: 'GPT',
  grok: 'Grok',
  llama: 'LLaMA',
  mini: 'Mini',
  nano: 'Nano',
  non: 'Non',
  opus: 'Opus',
  pro: 'Pro',
  reasoning: 'Reasoning',
  sonnet: 'Sonnet',
  turbo: 'Turbo',
  ultra: 'Ultra',
};

// Regex only runs on short model segments, so ReDoS risk is negligible.
// eslint-disable-next-line security/detect-unsafe-regex
const NUMERIC_SEGMENT_PATTERN = /^[0-9]+(?:\.[0-9]+)?$/;
const LOWERCASE_SEGMENT_PATTERN = /^[a-z]+$/;
const ALL_CAPS_SEGMENT_PATTERN = /^[A-Z]+$/;

const isNumericSegment = (segment: string): boolean => NUMERIC_SEGMENT_PATTERN.test(segment);

const toTitle = (segment: string): string => segment.charAt(0).toUpperCase() + segment.slice(1);

const formatModelSegment = (segment: string): string => {
  const normalized = toTrimmedNonEmptyString(segment) ?? '';
  if (!normalized) {
    return '';
  }
  const lower = normalized.toLowerCase();
  if (MODEL_SEGMENT_OVERRIDES[lower]) {
    return MODEL_SEGMENT_OVERRIDES[lower];
  }
  if (isNumericSegment(normalized)) {
    return normalized;
  }
  if (LOWERCASE_SEGMENT_PATTERN.test(normalized)) {
    return toTitle(normalized);
  }
  return normalized;
};

const formatProviderModelDisplayName = (providerModelId: string): string => {
  const trimmed = toTrimmedNonEmptyString(providerModelId);
  if (!trimmed) {
    return providerModelId;
  }
  const [providerRaw, ...rest] = trimmed.split('/');
  if (!providerRaw) {
    return trimmed;
  }
  const providerLabel = PROVIDER_DISPLAY_NAMES[providerRaw] ?? toTitle(providerRaw);
  const modelRaw = rest.join('/');
  if (!modelRaw) {
    return providerLabel;
  }
  const segments = modelRaw
    .replaceAll('_', '-')
    .split('-')
    .filter((segment) => segment.length > 0);
  if (segments.length === 0) {
    return providerLabel;
  }
  const formattedSegments = segments.map((segment) => formatModelSegment(segment));
  let modelLabel = '';
  for (let index = 0; index < formattedSegments.length; index += 1) {
    const segment = formattedSegments[index];
    if (!segment) {
      continue;
    }
    if (index === 0) {
      modelLabel = segment;
      continue;
    }
    const originalCurrent = segments[index];
    const originalPrevious = segments[index - 1];
    const previousFormatted = formattedSegments[index - 1] ?? '';
    const shouldHyphenate =
      (originalCurrent && isNumericSegment(originalCurrent) && ALL_CAPS_SEGMENT_PATTERN.test(previousFormatted)) ||
      (originalPrevious?.toLowerCase() === 'non' && originalCurrent?.toLowerCase() === 'reasoning');
    modelLabel = shouldHyphenate ? `${modelLabel}-${segment}` : `${modelLabel} ${segment}`;
  }
  return modelLabel.length > 0 ? `${providerLabel} ${modelLabel}` : providerLabel;
};

const getConfiguredModelLabel = (modelId: string): string => {
  const trimmed = toTrimmedNonEmptyString(modelId);
  if (!trimmed) {
    return 'Configured Model';
  }
  const runtimeDisplayName = formatProviderModelDisplayName(trimmed);
  const [providerRaw] = trimmed.split('/');
  if (providerRaw) {
    const providerLabel = PROVIDER_DISPLAY_NAMES[providerRaw] ?? toTitle(providerRaw);
    if (runtimeDisplayName.startsWith(`${providerLabel} `)) {
      const candidate = runtimeDisplayName.slice(providerLabel.length + 1);
      if (candidate.length > 0) {
        return candidate;
      }
    }
  }
  if (runtimeDisplayName.length > 0) {
    return runtimeDisplayName;
  }
  return `Configured Model (${trimmed})`;
};

const applyModelPatch = (registry: Map<string, ModelDefinition>, id: string, patch: ModelDefinitionPatch) => {
  const existing = registry.get(id);
  if (existing) {
    const mergedCapabilities = mergeCapabilities(existing.capabilities, patch.capabilities);
    const mergedAliasKeys = mergeStringSets(existing.aliasKeys, patch.aliasKeys);
    const mergedAlternateIds = mergeStringSets(existing.alternateIds, patch.alternateIds);
    const mergedDefaultConfig = mergeDefaultConfig(existing.defaultConfig, patch.defaultConfig);

    const nextDefinition: ModelDefinition = {
      ...existing,
      ...patch,
      provider: patch.provider ?? existing.provider,
      label: patch.label ?? existing.label,
      description: patch.description ?? existing.description,
      tier: patch.tier ?? existing.tier,
    };

    const providerModelId = patch.providerModelId ?? existing.providerModelId;
    assignOptionalField(nextDefinition, 'providerModelId', providerModelId);
    assignOptionalField(nextDefinition, 'defaultConfig', mergedDefaultConfig);

    const defaultEnabled = patch.defaultEnabled ?? existing.defaultEnabled;
    assignOptionalField(nextDefinition, 'defaultEnabled', defaultEnabled);

    const isDefault = patch.isDefault ?? existing.isDefault;
    assignOptionalField(nextDefinition, 'isDefault', isDefault);

    const isFallback = patch.isFallback ?? existing.isFallback;
    assignOptionalField(nextDefinition, 'isFallback', isFallback);

    assignOptionalField(nextDefinition, 'capabilities', mergedCapabilities);
    assignOptionalField(nextDefinition, 'aliasKeys', mergedAliasKeys);
    assignOptionalField(nextDefinition, 'alternateIds', mergedAlternateIds);

    const resolvedRole = patch.role ?? existing.role;
    assignOptionalField(nextDefinition, 'role', resolvedRole);

    registry.set(id, nextDefinition);
    return;
  }

  const provider = patch.provider ?? inferProviderFromModelId(id);
  const tier = patch.tier ?? 'standard';
  const label = patch.label ?? getConfiguredModelLabel(id);
  const runtimeDisplayName = formatProviderModelDisplayName(id);
  const runtimeLabel = toTrimmedNonEmptyString(runtimeDisplayName);
  const description =
    toTrimmedNonEmptyString(patch.description) ??
    (runtimeLabel
      ? `Runtime configured model definition (${runtimeDisplayName}).`
      : 'Runtime configured model definition with limited metadata available.');
  const defaultConfig = patch.defaultConfig ? cloneDefaultConfig(patch.defaultConfig) : undefined;

  const nextDefinition: ModelDefinition = {
    id,
    provider,
    label,
    description,
    tier,
  };

  if (patch.providerModelId) {
    nextDefinition.providerModelId = patch.providerModelId;
  }

  const resolvedCapabilities = toArray(patch.capabilities);
  if (resolvedCapabilities.length > 0) {
    nextDefinition.capabilities = resolvedCapabilities;
  }

  const resolvedAliasKeys = patch.aliasKeys ? toArray(patch.aliasKeys) : undefined;
  if (resolvedAliasKeys && resolvedAliasKeys.length > 0) {
    nextDefinition.aliasKeys = resolvedAliasKeys;
  }

  const resolvedAlternateIds = patch.alternateIds ? toArray(patch.alternateIds) : undefined;
  if (resolvedAlternateIds && resolvedAlternateIds.length > 0) {
    nextDefinition.alternateIds = resolvedAlternateIds;
  }

  if (patch.role) {
    nextDefinition.role = patch.role;
  }

  nextDefinition.defaultEnabled = patch.defaultEnabled ?? true;
  nextDefinition.isDefault = patch.isDefault ?? false;
  nextDefinition.isFallback = patch.isFallback ?? false;

  if (defaultConfig) {
    nextDefinition.defaultConfig = defaultConfig;
  }

  registry.set(id, nextDefinition);
};

const buildModelRegistry = (): readonly ModelDefinition[] => {
  const registry = new Map<string, ModelDefinition>();

  for (const definition of BASE_MODEL_DEFINITIONS) {
    registry.set(definition.id, { ...definition });
  }

  const existingPremiumDefinition = registry.get(CONFIGURED_PREMIUM_MODEL_ID);
  const configuredPremiumRuntimeName = existingPremiumDefinition?.providerModelId
    ? formatProviderModelDisplayName(existingPremiumDefinition.providerModelId)
    : formatProviderModelDisplayName(CONFIGURED_PREMIUM_MODEL_ID);
  const configuredPremiumLabel =
    existingPremiumDefinition?.label ?? getConfiguredModelLabel(CONFIGURED_PREMIUM_MODEL_ID);

  applyModelPatch(registry, CONFIGURED_PREMIUM_MODEL_ID, {
    provider: existingPremiumDefinition?.provider ?? inferProviderFromModelId(CONFIGURED_PREMIUM_MODEL_ID),
    label: configuredPremiumLabel,
    description:
      existingPremiumDefinition?.description ?? `Primary reasoning model (currently ${configuredPremiumRuntimeName}).`,
    tier: 'premium',
    providerModelId: existingPremiumDefinition?.providerModelId ?? CONFIGURED_PREMIUM_MODEL_ID,
    capabilities:
      existingPremiumDefinition?.capabilities && existingPremiumDefinition.capabilities.length > 0
        ? toArray(existingPremiumDefinition.capabilities)
        : ['reasoning'],
    defaultEnabled: true,
    isDefault: existingPremiumDefinition?.isDefault ?? false,
    isFallback: existingPremiumDefinition?.isFallback ?? false,
    aliasKeys: [MODEL_ROLES.PREMIUM, 'premium', 'premium-reasoning', 'primary-reasoning-model'],
    alternateIds: toArray(existingPremiumDefinition?.alternateIds),
    role: MODEL_ROLES.PREMIUM,
    defaultConfig: existingPremiumDefinition?.defaultConfig ?? {
      maxTokens: 400_000,
    },
  });

  const existingStandardDefinition = registry.get(CONFIGURED_STANDARD_MODEL_ID);
  const configuredStandardRuntimeName = existingStandardDefinition?.providerModelId
    ? formatProviderModelDisplayName(existingStandardDefinition.providerModelId)
    : formatProviderModelDisplayName(CONFIGURED_STANDARD_MODEL_ID);
  const configuredStandardLabel =
    existingStandardDefinition?.label ?? getConfiguredModelLabel(CONFIGURED_STANDARD_MODEL_ID);

  applyModelPatch(registry, CONFIGURED_STANDARD_MODEL_ID, {
    provider: existingStandardDefinition?.provider ?? inferProviderFromModelId(CONFIGURED_STANDARD_MODEL_ID),
    label: configuredStandardLabel,
    description:
      existingStandardDefinition?.description ??
      `Balanced general-purpose model (currently ${configuredStandardRuntimeName}).`,
    tier: 'standard',
    providerModelId: existingStandardDefinition?.providerModelId ?? CONFIGURED_STANDARD_MODEL_ID,
    capabilities:
      existingStandardDefinition?.capabilities && existingStandardDefinition.capabilities.length > 0
        ? toArray(existingStandardDefinition.capabilities)
        : ['speed'],
    defaultEnabled: true,
    isDefault: existingStandardDefinition?.isDefault ?? false,
    isFallback: existingStandardDefinition?.isFallback ?? true,
    aliasKeys: [MODEL_ROLES.STANDARD, 'balanced', 'standard-reasoning-model'],
    alternateIds: toArray(existingStandardDefinition?.alternateIds),
    role: MODEL_ROLES.STANDARD,
    defaultConfig: existingStandardDefinition?.defaultConfig ?? {
      maxTokens: 400_000,
    },
  });

  const result: ModelDefinition[] = [];
  for (const value of registry.values()) {
    result.push(value);
  }
  return result;
};

const MODEL_REGISTRY: readonly ModelDefinition[] = buildModelRegistry();

export type ModelId = (typeof MODEL_REGISTRY)[number]['id'];

export const MODEL_REGISTRY_BY_ID: Record<string, (typeof MODEL_REGISTRY)[number]> = Object.fromEntries(
  MODEL_REGISTRY.map((definition) => [definition.id, definition])
);

// Override runtime labels when we want to surface a friendlier alias but keep the canonical id
// intact for downstream provider lookups.
const MODEL_RUNTIME_DISPLAY_OVERRIDES: Partial<Record<ModelId, string>> = Object.freeze({
  'model.reasoning.fast': 'xAI Grok 4.1 Fast',
});

// Build lookup map so any alternate id resolves back to the canonical model id.
const aliasEntries = MODEL_REGISTRY.flatMap((definition) =>
  toArray(definition.alternateIds).map((alias) => [alias, definition.id] as const)
);

export const MODEL_ALIAS_MAP: Record<string, ModelId> = Object.fromEntries(aliasEntries);

const buildAliasAndRoleMaps = () => {
  const aliasMap: Record<string, ModelId> = {};
  const roleMap: Record<string, ModelRole> = {};
  for (const definition of MODEL_REGISTRY) {
    const aliasKeys = toArray(definition.aliasKeys);
    for (const key of aliasKeys) {
      aliasMap[key] = definition.id;
    }
    if (definition.role) {
      roleMap[definition.id] = definition.role;
    }
  }
  return { aliasMap, roleMap };
};

const aiModels = buildAliasAndRoleMaps();

export const AI_MODELS = Object.freeze(aiModels.aliasMap);

export const MODEL_ROLE_BY_ID: Record<string, ModelRole | undefined> = Object.freeze({ ...aiModels.roleMap });

export type AIModel = (typeof AI_MODELS)[keyof typeof AI_MODELS];

export const DEFAULT_MODEL_ID: ModelId =
  MODEL_REGISTRY.find((model) => model.isDefault)?.id ?? CONFIGURED_PREMIUM_MODEL_ID;

export const FALLBACK_MODEL_ID: ModelId =
  MODEL_REGISTRY.find((model) => model.isFallback)?.id ?? CONFIGURED_STANDARD_MODEL_ID;

export const DEFAULT_ENABLED_MODEL_IDS: readonly ModelId[] = MODEL_REGISTRY.filter(
  (model) => model.defaultEnabled === true
).map((model) => model.id);

export const MODEL_DISPLAY_NAMES: Record<string, string> = Object.fromEntries(
  MODEL_REGISTRY.map((model) => [model.id, model.label] as const)
);

export function getProviderFromModel(model: string): string {
  const definition = MODEL_REGISTRY_BY_ID[model];
  if (definition) {
    return definition.provider;
  }
  const [provider] = model.split('/');
  return provider || 'custom';
}

export function getModelDisplayName(model: string): string {
  return MODEL_DISPLAY_NAMES[model] ?? model;
}

export function canonicalizeModelId(candidate: string | null | undefined): ModelId | null {
  const trimmed = toTrimmedNonEmptyString(candidate);
  if (!trimmed) return null;
  const direct = MODEL_REGISTRY_BY_ID[trimmed];
  if (direct) return direct.id;
  const alias = MODEL_ALIAS_MAP[trimmed];
  return alias ?? null;
}

export function getModelRuntimeDisplayName(model: string): string {
  const canonicalId = canonicalizeModelId(model);
  if (canonicalId) {
    const displayOverride = MODEL_RUNTIME_DISPLAY_OVERRIDES[canonicalId];
    const trimmedOverride = toTrimmedNonEmptyString(displayOverride);
    if (trimmedOverride) {
      return trimmedOverride;
    }
    const definition = MODEL_REGISTRY_BY_ID[canonicalId];
    if (definition) {
      const providerModelId = toTrimmedNonEmptyString(definition.providerModelId) ?? '';
      if (providerModelId) {
        return formatProviderModelDisplayName(providerModelId);
      }
      if (definition.label) {
        return definition.label;
      }
    }
    return formatProviderModelDisplayName(canonicalId);
  }

  if (model.includes('/')) {
    return formatProviderModelDisplayName(model);
  }
  return model;
}

export function supportsReasoning(model: string): boolean {
  const definition = MODEL_REGISTRY_BY_ID[model];
  if (!definition) {
    return false;
  }
  const capabilities: readonly ModelCapability[] = toArray<ModelCapability>(
    definition.capabilities as readonly ModelCapability[] | null | undefined
  );
  return capabilities.includes('reasoning');
}

export function isPremiumModelId(model: string): boolean {
  const definition = MODEL_REGISTRY_BY_ID[model];
  return definition?.tier === 'premium';
}

export function getModelOptions(): Array<{
  value: ModelId;
  label: string;
  description: string;
  provider: ModelProvider;
  tier: ModelTier;
  capabilities: readonly ModelCapability[];
  defaultEnabled: boolean;
  defaultConfig: ModelDefinition['defaultConfig'];
}> {
  return MODEL_REGISTRY.map((model) => ({
    value: model.id,
    label: model.label,
    description: model.description,
    provider: model.provider,
    tier: model.tier,
    capabilities: toArray<ModelCapability>(model.capabilities as readonly ModelCapability[] | null | undefined),
    defaultEnabled: model.defaultEnabled === true,
    defaultConfig: model.defaultConfig,
  }));
}

type ModelConfigTemplate = NonNullable<ModelDefinition['defaultConfig']> & { providerModelId?: string };

export function getDefaultModelConfigs(): Record<ModelId, ModelConfigTemplate> {
  const configs: Record<ModelId, ModelConfigTemplate> = {};
  for (const model of MODEL_REGISTRY) {
    let baseConfig: ModelConfigTemplate | undefined;

    if (model.defaultConfig) {
      baseConfig = { ...model.defaultConfig };
    }

    if (model.providerModelId) {
      baseConfig = baseConfig ?? ({} as ModelConfigTemplate);
      baseConfig.providerModelId = model.providerModelId;
    }

    if (baseConfig && Object.keys(baseConfig).length > 0) {
      configs[model.id] = baseConfig satisfies ModelConfigTemplate;
    }
  }
  return configs;
}

export function getModelIdForRole(role: ModelRole): ModelId {
  return MODEL_ID_BY_ROLE[role];
}

export function getModelDefinition(modelId: string): ModelDefinition | undefined {
  return MODEL_REGISTRY_BY_ID[modelId];
}

export function getRoleForModelId(modelId: string): ModelRole | undefined {
  return MODEL_ROLE_BY_ID[modelId];
}

export function getMaxTokensForModel(modelId: string): number | undefined {
  const definition = getModelDefinition(modelId);
  return definition?.defaultConfig?.maxTokens;
}

export function getDisplayNameForRole(role: ModelRole): string {
  const modelId = getModelIdForRole(role);
  if (modelId === undefined) {
    return role;
  }
  return getModelDisplayName(modelId);
}
